import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore";
import React, { useEffect, useState, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { db } from "../firebase";
import AdminService from "../service/admin/adminService";
import mailer from "../service/api/mailer";
import dataService from "../service/data/DataService";
import { encrypt } from "../service/encryption/Encryption";
import CustomInput from "./CustomInput";
import CustomSelect from "./CustomSelect";
import DatePickerTemplate from "./DatePickerTemplate";
import { UserStatus } from "../utils/enums";
import Modal from "./Modal";

export const CitizenForm = ({
  onSubmitSuccess,
  centerState,
  centerCounty,
  centerId,
  centerEmail,
  fromCitizenSearchRegular = false,
}) => {
  const organizationType = localStorage.getItem("organizationType");
  const organizationId = localStorage.getItem("organizationId");
  const organizationState =
    centerState || localStorage.getItem("organizationState");
  const organizationCounty =
    centerCounty || localStorage.getItem("organizationCounty");

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    mobile_number: "",
    alternate_phone: "",
    address1: "",
    address2: "",
    city: "",
    state: organizationState,
    zip: "",
    county: organizationCounty,
    birth_date: "",
    gender: "Male",
    ethnicity: "African American or Black",
    citizenship: "US Citizen",
    disability: false,
    military_vet: false,
    alternate_contact_name: "",
    alternate_email: "",
    alternate_mobile_number: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [emailExists, setEmailExists] = useState(false);
  const [isDuplicateEmail, setIsDuplicateEmail] = useState(false);
  const [isDuplicateAddress, setIsDuplicateAddress] = useState(false);
  const [dupAddressData, setDupAddressData] = useState(null);
  const [dupAddressBackupData, setDupAddressBackupData] = useState(null);
  const [selectedDisasters, setSelectedDisasters] = useState([]);
  const [disasterOptions, setDisasterOptions] = useState([]);
  const [popupMessage, setPopupMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});
  const [hasNoEmail, setHasNoEmail] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [credentials, setCredentials] = useState({
    darciId: "",
    password: "",
  });

  // Consolidate required fields definition
  const requiredFields = useMemo(
    () => [
      "first_name",
      "last_name",
      ...(hasNoEmail ? [] : ["email"]), // Only include email if hasNoEmail is false
      "mobile_number",
      "address1",
      "city",
      "zip",
      "birth_date",
      "gender",
      "ethnicity",
    ],
    [hasNoEmail]
  ); // Add hasNoEmail as dependency

  useEffect(() => {
    const storedDisasters = localStorage.getItem("selectedDisasters");
    if (storedDisasters) {
      const disasters = JSON.parse(storedDisasters);
      setSelectedDisasters(disasters);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Helper function to update errors and touched fields
  const updateFieldStatus = (field, error = null) => {
    setErrors((prev) =>
      error
        ? {
            ...prev,
            [field]: error,
          }
        : {
            ...prev,
            [field]: undefined,
          }
    );

    setTouchedFields((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  // Helper function to clear field status
  const clearFieldStatus = (field) => {
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
    setTouchedFields((prev) => {
      const newTouched = { ...prev };
      delete newTouched[field];
      return newTouched;
    });
  };

  // Consolidate validation logic
  const validateForm = () => {
    const missing = requiredFields.filter((field) => !formData[field]);

    // Mark all required fields as touched
    const newTouchedFields = {};
    requiredFields.forEach((field) => {
      newTouchedFields[field] = true;
    });
    setTouchedFields(newTouchedFields);

    if (missing.length > 0) {
      setMissingFields(missing);
      setErrors(
        missing.reduce(
          (acc, field) => ({
            ...acc,
            [field]: "This field is required",
          }),
          {}
        )
      );
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields first
    const isValid = validateForm();

    // Check for duplicate email only if user has email
    const emailCheck = !hasNoEmail ? await checkEmailExistence() : false;
    if (emailCheck) {
      updateFieldStatus("email", { message: "Email already in use" });
      setTimeout(() => clearFieldStatus("email"), 3000);
    }

    // If either validation failed or email exists, don't proceed
    if (!isValid || emailCheck) {
      return;
    }

    try {
      setIsSubmitting(true);

      // Format birth_date to mm/dd/yyyy
      const formattedBirthDate = formData.birth_date
        ? new Date(formData.birth_date).toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
            minimumIntegerDigits: 2,
          })
        : "";

      // Check for duplicates
      const addressCheck = await checkDupAddress();
      if (addressCheck) {
        setIsDuplicateAddress(true);
        return;
      }

      // Get next DARCI ID
      const darciNo = (await AdminService.getDarciNo()) + 1;
      const tempPassword = generateOtp();

      // Generate unique IDs for user, address, and household
      const userId = doc(collection(db, "users")).id;
      const addressId = doc(collection(db, "address")).id;
      const householdId = doc(collection(db, "household")).id;
      const householdMemberId = doc(collection(db, "household_members")).id;
      // console.log("Generated IDs:", { userId, addressId, householdId });

      // Create user document
      const userData = {
        ...formData,
        birth_date: formattedBirthDate,
        darci_id: darciNo.toString(),
        user_role: "citizen",
        created_date: serverTimestamp(),
        created_by: "center",
        status: UserStatus.ADDRESS_VERIFIED,
        state: organizationState,
        county: organizationCounty,
        otp: encrypt(generateOtp()),
        password: encrypt(tempPassword),
        center_id: centerId,
        pin: encrypt("7777"),
        id: userId,
        address_id: addressId,
        household_id: householdId,
        household_member_id: householdMemberId,
      };

      // console.log("formData email: ", formData.email);
      // console.log("darciNo: ", darciNo);
      // console.log("formData first_name: ", formData.first_name);
      // console.log("tempPassword is here: ", tempPassword);

      // Save user to Firestore
      await setDoc(doc(db, "users", userId), userData);

      // Create address document in Firestore
      await setDoc(doc(db, "address", addressId), {
        user_id: userId,
        created_at: serverTimestamp(),
      });

      // Create primary address in the nested addresses collection
      const addressesCollectionRef = collection(
        db,
        `address/${addressId}/addresses`
      );
      const addressDocRef = doc(addressesCollectionRef);
      await setDoc(addressDocRef, {
        is_primary_address: true,
        address1: formData.address1,
        address2: formData.address2 || "",
        city: formData.city,
        state: organizationState,
        zip: formData.zip,
        county: organizationCounty,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });

      // Create household member document in Firestore
      const householdMembersRef = collection(
        db,
        "household",
        householdId,
        "household_members"
      );
      const householdMemberRef = doc(householdMembersRef);
      await setDoc(householdMemberRef, {
        date_of_birth: formattedBirthDate,
        disability: formData.disability,
        first_name: formData.first_name,
        last_name: formData.last_name,
        household_member_id: householdMemberId,
        military_vet: formData.military_vet,
        relationship: "",
        acc_created: true,
        email: formData.email ? formData.email : "",
      });

      await setDoc(doc(db, "users-list", userId), {
        created_date: serverTimestamp(),
        created_by: "center",
        email: formData.email || "",
        user_id: userId,
      });

      const fullName = `${formData.first_name} ${formData.last_name}`;

      // Only send email if user has an email address
      await mailer.sendCaseMgtEmailInvite(
        hasNoEmail ? centerEmail : formData.email,
        fullName,
        darciNo.toString(),
        tempPassword
      );

      // Increment DARCI number
      await AdminService.incrementDarciNo();

      if (!fromCitizenSearchRegular) {
        // Generate case number
        const newCaseNumber = await dataService.generateCaseNumber();

        // Create case management entry
        const caseManagementRef = collection(db, "case_management");
        const caseData = {
          case_number: newCaseNumber,
          citizen_id: userId,
          citizen_name: fullName,
          citizen_email: formData.email || "",
          citizen_address: formData.address1,
          citizen_mobile: formData.mobile_number,
          disaster_id: selectedDisasters[0],
          disaster_name: localStorage.getItem("disasterName"),
          center_id: centerId,
          status: "New Case",
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        };

        await addDoc(caseManagementRef, caseData);

        // Create impacted-addresses document for the selected disaster
        if (selectedDisasters && selectedDisasters.length > 0) {
          const disasterId = selectedDisasters[0];
          const impactedAddressRef = doc(
            db,
            `users/${userId}/impacted-addresses/${disasterId}`
          );

          await setDoc(impactedAddressRef, {
            address1: formData.address1,
            address2: formData.address2 || "",
            city: formData.city,
            state: organizationState,
            zip: formData.zip,
            county: organizationCounty,
            impacted_address_id: addressDocRef.id, // Use the ID of the nested address document
            created_at: serverTimestamp(),
            updated_at: serverTimestamp(),
            is_deleted: false,
          });
        }

        // Create survey response entry
        const surveyData = {
          user_id: userId,
          disasters: [
            {
              disaster_id: selectedDisasters[0],
              disaster_name: localStorage.getItem("disasterName"),
              disaster_type:
                disasterOptions.find((d) => d.id === selectedDisasters[0])
                  ?.disaster_type || "",
            },
          ],
          survey_id: uuidv4(),
          case_number: newCaseNumber,
          submitted_at: serverTimestamp(),
          responses: {}, // Empty responses object
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        };

        const surveyDocRef = doc(
          db,
          `users/${userId}/survey_responses`,
          surveyData.survey_id
        );

        await setDoc(surveyDocRef, surveyData);

        const centerDetails = JSON.parse(sessionStorage.getItem("center"));

        if (!!centerDetails) {
          const { id, email, first_name, name_of_org } = centerDetails || {};
          const caseNotifRef = collection(db, "message-center");
          await addDoc(caseNotifRef, {
            content: "A new case has been added.",
            center_id: id,
            sender: {
              email,
              id,
              name: first_name,
              organization: name_of_org,
              role: "center",
            },
            viewer: [],
            timestamp: serverTimestamp(),
            title: "New Case Added",
            type: "case_supervisor_message",
          });
        }

        // Show success message
        setPopupMessage(
          `Citizen successfully registered with case number: ${newCaseNumber}`
        );
        // Clear form
        setFormData({
          first_name: "",
          last_name: "",
          email: "",
          mobile_number: "",
          alternate_phone: "",
          address1: "",
          address2: "",
          city: "",
          zip: "",
          birth_date: "",
          gender: "",
          ethnicity: "",
          citizenship: "",
          disability: false,
          military_vet: false,
          alternate_contact_name: "",
          alternate_email: "",
          alternate_mobile_number: "",
        });
      }

      // Store credentials for modal and show modal
      setCredentials({
        darciId: darciNo.toString(),
        password: tempPassword,
      });
      setShowCredentialsModal(true);
    } catch (error) {
      console.error("Error creating citizen:", error);
      setPopupMessage("Error creating citizen. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const checkDupAddress = async () => {
    // Get all users and convert their addresses to lowercase for comparison
    const usersRef = collection(db, "users");
    const citizenQuery = query(usersRef, where("user_role", "==", "citizen"));
    const snapshot = await getDocs(citizenQuery);

    // Check each document manually with case-insensitive comparison
    const matchingDoc = snapshot.docs.find((doc) => {
      const userData = doc.data();
      return (
        userData.address1?.toLowerCase() ===
          formData.address1.trim().toLowerCase() &&
        userData.city?.toLowerCase() === formData.city.trim().toLowerCase() &&
        userData.state?.toLowerCase() === formData.state.toLowerCase() &&
        userData.zip?.toLowerCase() === formData.zip.trim().toLowerCase()
      );
    });

    if (matchingDoc) {
      // Set the duplicate address flag
      setIsDuplicateAddress(true);
      try {
        const existingUser = matchingDoc.data();
        const firstName = existingUser.first_name;
        const email = existingUser.email;
        const otp = generateOtp();

        // Generate a secure verification token
        const verificationToken =
          Math.random().toString(36).substring(2, 15) +
          Math.random().toString(36).substring(2, 15);

        setDupAddressBackupData({
          dup_address_otp: otp,
          dup_address_token: verificationToken,
          dup_address_original_email: email,
          dup_address_fraud_email: formData.email,
        });

        // Store the verification data
        const verificationData = {
          original_email: email,
          fraud_email: formData.email,
          created_at: new Date().toISOString(),
          otp: otp,
          token: verificationToken,
        };

        setDupAddressData(verificationData);

        // Create a base64 encoded string of the verification data
        const encodedData = btoa(JSON.stringify(verificationData));
        const loginLink = `${window.location.origin}/login?dupAddress=true&token=${verificationToken}&data=${encodedData}`;

        // Send OTP to duplicate email
        if (!emailExists) {
          await mailer.sendOtpDuplicateEmail(email, otp, firstName, loginLink);
        }
        return true;
      } catch (error) {
        console.error("Error in checkDupAddress:", error);
        return false;
      }
    }
    return false;
  };

  const checkEmailExistence = async () => {
    // Skip email check if hasNoEmail is true
    if (hasNoEmail || !formData.email) return false;

    const email = formData.email;
    const snapshot = await getDocs(
      query(collection(db, "users"), where("email", "==", email))
    );
    if (snapshot.empty) {
      setEmailExists(false);
      setIsDuplicateEmail(false);
      return false;
    } else {
      setErrors((prev) => ({
        ...prev,
        email: { message: "Email already in use" },
      }));
      setEmailExists(true);
      setIsDuplicateEmail(true);
      return true;
    }
  };

  const generateOtp = () => {
    return Math.floor(Math.random() * 900000 + 100000).toString();
  };

  return (
    <div className="w-full">
      <div className="flex flex-col gap-4 p-4">
        {/* First row */}
        <div className="flex gap-4">
          <div className="w-1/3">
            <CustomInput
              placeholder="First Name *"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              errors={errors}
              register={{
                name: "first_name",
                onChange: handleChange,
              }}
            />
          </div>

          <div className="w-1/3">
            <CustomInput
              placeholder="Last Name *"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              errors={errors}
              register={{
                name: "last_name",
                onChange: handleChange,
              }}
            />
          </div>

          <div className="w-1/3">
            <DatePickerTemplate
              placeholder="Date of Birth *"
              name="birth_date"
              value={formData.birth_date}
              onChange={(val) => {
                setFormData((prev) => ({
                  ...prev,
                  birth_date: val,
                }));
              }}
              errors={errors}
            />
          </div>
        </div>

        {/* Contact row */}
        <div className="flex gap-4">
          {!hasNoEmail && (
            <div className="w-1/3">
              <CustomInput
                placeholder="Email Address *"
                name="email"
                value={formData.email}
                onChange={handleChange}
                errors={errors}
                register={{
                  name: "email",
                  onChange: handleChange,
                }}
              />
            </div>
          )}

          <div className={`w-${hasNoEmail ? "1/2" : "1/3"}`}>
            <CustomInput
              placeholder="Phone No. *"
              name="mobile_number"
              value={formData.mobile_number}
              onChange={handleChange}
              errors={errors}
              register={{
                name: "mobile_number",
                onChange: handleChange,
              }}
            />
          </div>

          <div className={`w-${hasNoEmail ? "1/2" : "1/3"}`}>
            <CustomInput
              placeholder="Alternate Phone No."
              name="alternate_phone"
              value={formData.alternate_phone}
              onChange={handleChange}
              errors={errors}
              register={{
                name: "alternate_phone",
                onChange: handleChange,
              }}
            />
          </div>
        </div>

        {/* No Email Checkbox */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="noEmail"
            checked={hasNoEmail}
            onChange={(e) => {
              setHasNoEmail(e.target.checked);
              if (e.target.checked) {
                // Clear email field when checkbox is checked
                setFormData((prev) => ({
                  ...prev,
                  email: "",
                }));
                // Clear email error if it exists
                setErrors((prev) => ({
                  ...prev,
                  email: null,
                }));
              }
            }}
            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="noEmail" className="text-gray-700 text-sm">
            User does not have an email
          </label>
        </div>

        {/* Address rows */}
        <div className="w-full">
          <CustomInput
            placeholder="Address 1 *"
            name="address1"
            value={formData.address1}
            onChange={handleChange}
            errors={errors}
            register={{
              name: "address1",
              onChange: handleChange,
            }}
          />
        </div>

        <div className="w-full">
          <CustomInput
            placeholder="Address 2"
            name="address2"
            value={formData.address2}
            onChange={handleChange}
            errors={errors}
            register={{
              name: "address2",
              onChange: handleChange,
            }}
          />
        </div>

        {/* Location row */}
        <div className="flex gap-4">
          <div className="w-1/4">
            <CustomInput
              placeholder="City *"
              name="city"
              value={formData.city}
              onChange={handleChange}
              errors={errors}
              register={{
                name: "city",
                onChange: handleChange,
              }}
            />
          </div>

          <div className="w-1/4">
            <CustomInput
              placeholder="State"
              name="state"
              value={organizationState}
              disabled={true}
              errors={errors}
              register={{
                name: "state",
                onChange: handleChange,
              }}
            />
          </div>

          <div className="w-1/4">
            <CustomInput
              placeholder="ZIP Code *"
              name="zip"
              value={formData.zip}
              onChange={handleChange}
              errors={errors}
              register={{
                name: "zip",
                onChange: handleChange,
              }}
            />
          </div>

          <div className="w-1/4">
            <CustomInput
              placeholder="County"
              name="county"
              value={organizationCounty}
              disabled={true}
              errors={errors}
              register={{
                name: "county",
                onChange: handleChange,
              }}
            />
          </div>
        </div>

        {/* Demographics row */}
        <div className="flex gap-4">
          {/* <div className="w-1/3">
            <CustomSelect
              placeholder="Citizenship *"
              name="citizenship"
              value={formData.citizenship}
              onChange={handleChange}
              errors={errors}
              register={{
                name: "citizenship",
                onChange: handleChange,
              }}
              options={[
                { value: "US Citizen", label: "US Citizen" },
                { value: "Lawful Permanent Resident", label: "Lawful Permanent Resident" },
                { value: "Alien Authorized to Work", label: "Alien Authorized to Work" },
                { value: "Other", label: "Other" },
              ]}
            />
          </div> */}

          <div className="w-1/2">
            <CustomSelect
              placeholder="Ethnicity *"
              name="ethnicity"
              value={formData.ethnicity}
              onChange={handleChange}
              errors={errors}
              register={{
                name: "ethnicity",
                onChange: handleChange,
              }}
              options={[
                {
                  value: "African American or Black",
                  label: "African American or Black",
                },
                {
                  value: "American Indian or Alaska Native",
                  label: "American Indian or Alaska Native",
                },
                { value: "Asian", label: "Asian" },
                { value: "Hispanic/ Latino", label: "Hispanic/ Latino" },
                {
                  value: "Native Hawaiian or Pacific Islander",
                  label: "Native Hawaiian or Pacific Islander",
                },
                { value: "Tribal Affiliation", label: "Tribal Affiliation" },
                { value: "White", label: "White" },
                { value: "Other", label: "Other" },
                { value: "None of the Above", label: "None of the Above" },
              ]}
            />
          </div>

          <div className="w-1/2">
            <CustomSelect
              placeholder="Sex *"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              errors={errors}
              register={{
                name: "gender",
                onChange: handleChange,
              }}
              options={[
                { value: "Male", label: "Male" },
                { value: "Female", label: "Female" },
              ]}
            />
          </div>
        </div>

        {/* Status row */}
        <div className="flex gap-4">
          <div className="w-1/2">
            <label className="text-gray-700 mb-1 block text-sm font-medium">
              Do you have a disability?
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="disability"
                  value="Yes"
                  checked={formData.disability === true}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      disability: e.target.value === "Yes",
                    }));
                  }}
                  className="mr-2 cursor-pointer"
                />
                <span className="text-sm">Yes</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="disability"
                  value="No"
                  checked={formData.disability === false}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      disability: e.target.value === "Yes",
                    }));
                  }}
                  className="mr-2 cursor-pointer"
                />
                <span className="text-sm">No</span>
              </label>
            </div>
          </div>

          <div className="w-1/2">
            <label className="text-gray-700 mb-1 block text-sm font-medium">
              Are you Active Military / Vet?
            </label>
            <div className="flex gap-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="military_vet"
                  value="Yes"
                  checked={formData.military_vet === true}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      military_vet: e.target.value === "Yes",
                    }));
                  }}
                  className="mr-2 cursor-pointer"
                />
                <span className="text-sm">Yes</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="military_vet"
                  value="No"
                  checked={formData.military_vet === false}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      military_vet: e.target.value === "Yes",
                    }));
                  }}
                  className="mr-2 cursor-pointer"
                />
                <span className="text-sm">No</span>
              </label>
            </div>
          </div>
        </div>

        {/* Alternate Contact Section */}
        <div className="mb-2 mt-4">
          <h2 className="text-base font-bold text-[#003460]">
            Alternate Contact
          </h2>
        </div>

        <div className="flex gap-4">
          <div className="w-1/3">
            <CustomInput
              placeholder="Contact Name"
              name="alternate_contact_name"
              value={formData.alternate_contact_name}
              onChange={handleChange}
              errors={errors}
              register={{
                name: "alternate_contact_name",
                onChange: handleChange,
              }}
            />
          </div>

          <div className="w-1/3">
            <CustomInput
              placeholder="Email"
              name="alternate_email"
              value={formData.alternate_email}
              onChange={handleChange}
              errors={errors}
              register={{
                name: "alternate_email",
                onChange: handleChange,
              }}
            />
          </div>

          <div className="w-1/3">
            <CustomInput
              placeholder="Phone Number"
              name="alternate_mobile_number"
              value={formData.alternate_mobile_number}
              onChange={handleChange}
              errors={errors}
              register={{
                name: "alternate_mobile_number",
                onChange: handleChange,
              }}
            />
          </div>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-between  gap-x-4 border-t px-5">
        <button
          onClick={() => onSubmitSuccess()}
          className="text-gray-700 hover:bg-gray-50 w-full cursor-pointer rounded-[4px] border border-[#0a2558] bg-white px-4 py-4 text-xl font-medium"
        >
          Cancel
        </button>

        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className={`w-full cursor-pointer rounded-[4px] px-4 py-4 text-xl font-medium text-[#F7CA41] ${
            isSubmitting
              ? "bg-[#0a2558]/90"
              : "bg-[#0a2558] hover:bg-[#0a2558]/90"
          }`}
        >
          {isSubmitting ? "Saving..." : "Save"}
        </button>
      </div>

      {showCredentialsModal && (
        <Modal
          isOpen={showCredentialsModal}
          onClose={() => {
            setShowCredentialsModal(false);
            // Call onSubmitSuccess and clear form after closing credentials modal
            onSubmitSuccess();
            setFormData({
              first_name: "",
              last_name: "",
              email: "",
              mobile_number: "",
              alternate_phone: "",
              address1: "",
              address2: "",
              city: "",
              zip: "",
              birth_date: "",
              gender: "",
              ethnicity: "",
              citizenship: "",
              disability: false,
              military_vet: false,
              alternate_contact_name: "",
              alternate_email: "",
              alternate_mobile_number: "",
            });
          }}
          title="Citizen Credentials"
          customLeftButtonText="Close"
          hideRightButton={true}
          noFooter={true}
        >
          <div className="flex flex-col gap-4">
            <p className="text-lg">Citizen successfully registered!</p>
            <div className="bg-gray-50 rounded p-4">
              <p>
                <strong>DARCI ID:</strong> {credentials.darciId}
              </p>
              <p>
                <strong>Temporary Password:</strong> {credentials.password}
              </p>
            </div>
            <p className="text-gray-600 text-sm">
              Please make sure to securely share these credentials with the
              citizen.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
};
